// Mock API service that simulates backend calls with localStorage persistence
// Updated to fix cache issues

import { 
  User, Vendor, SKU, PurchaseOrder, QualityInspection, Invoice, GateEntry, HandoverLog,
  mockUsers, mockVendors, mockSKUs, mockPurchaseOrders, 
  mockQualityInspections, mockInvoices, mockGateEntries, mockReceivedInvoices, mockHandoverLogs,
  getSKUById, getVendorById, getUserById, getPOById
} from '@/data/mockData';
import { StorageManager, STORAGE_KEYS } from '@/utils/localStorage';
import { MockAPI } from '@/api/mockApi';

// API Response interface
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  total?: number;
  page?: number;
  limit?: number;
}

// Filter and pagination interfaces
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface SKUFilters extends PaginationParams {
  category?: string;
  search?: string;
  status?: 'active' | 'inactive' | 'all';
  lowStock?: boolean;
  vendorId?: number;
}

export interface VendorFilters extends PaginationParams {
  search?: string;
  status?: 'active' | 'inactive' | 'all';
  performanceRating?: number;
}

export interface POFilters extends PaginationParams {
  status?: string;
  vendorId?: number;
  dateFrom?: string;
  dateTo?: string;
  priority?: string;
  createdBy?: number;
}

// Utility function to simulate API delay
const simulateDelay = (ms: number = 300): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Initialize data in localStorage if not exists
const initializeData = () => {
  StorageManager.initializeDefaults('USERS', mockUsers);
  StorageManager.initializeDefaults('VENDORS', mockVendors);
  StorageManager.initializeDefaults('SKUS', mockSKUs);
  
  // Initialize PURCHASE_ORDERS only if missing to avoid overwriting user-created POs
  StorageManager.initializeDefaults('PURCHASE_ORDERS', mockPurchaseOrders);
  
  StorageManager.initializeDefaults('QUALITY_INSPECTIONS', mockQualityInspections);
  StorageManager.initializeDefaults('INVOICES', mockInvoices);
  StorageManager.initializeDefaults('GATE_ENTRIES', mockGateEntries);
  StorageManager.initializeDefaults('HANDOVER_LOGS', mockHandoverLogs);
};

// Generic helper functions
const applyPagination = <T>(data: T[], page: number = 1, limit: number = 10): { items: T[], total: number } => {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  return {
    items: data.slice(startIndex, endIndex),
    total: data.length
  };
};

const applySorting = <T>(data: T[], sortBy?: string, sortOrder: 'asc' | 'desc' = 'asc'): T[] => {
  if (!sortBy) return data;
  
  return [...data].sort((a, b) => {
    const aVal = (a as any)[sortBy];
    const bVal = (b as any)[sortBy];
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};

export class MockAPIService {
  // Initialize the service
  static initialize() {
    initializeData();
  }

  // Authentication
  static async login(username: string, password: string): Promise<ApiResponse<User | null>> {
    await simulateDelay(500);
    
    const users = StorageManager.load<User[]>('USERS', []) || [];
    const user = users.find(u => u.username === username && u.password === password && u.isActive);
    
    if (user) {
      // Update last login
      user.lastLogin = new Date().toISOString();
      const updatedUsers = users.map(u => u.id === user.id ? user : u);
      StorageManager.save('USERS', updatedUsers);
      
      return {
        data: user,
        success: true,
        message: 'Login successful'
      };
    }
    
    return {
      data: null,
      success: false,
      message: 'Invalid credentials'
    };
  }

  static async logout(): Promise<ApiResponse<boolean>> {
    await simulateDelay(200);
    return {
      data: true,
      success: true,
      message: 'Logged out successfully'
    };
  }

  // SKU Management
  static async getSKUs(filters: SKUFilters = {}): Promise<ApiResponse<SKU[]>> {
    await simulateDelay();
    
    let skus = StorageManager.load<SKU[]>('SKUS', []) || [];
    
    // Only initialize with mock data if localStorage is completely empty
    if (skus.length === 0) {
      skus = [...mockSKUs];
      StorageManager.save('SKUS', skus);
    }
    
    // Note: Removed the auto-update logic that was overwriting user data
    
    // Apply filters
    if (filters.category) {
      skus = skus.filter(sku => sku.category === filters.category);
    }
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      skus = skus.filter(sku => 
        sku.name.toLowerCase().includes(search) ||
        sku.skuCode.toLowerCase().includes(search) ||
        sku.description.toLowerCase().includes(search)
      );
    }
    
    if (filters.status && filters.status !== 'all') {
      skus = skus.filter(sku => 
        filters.status === 'active' ? sku.isActive : !sku.isActive
      );
    }
    
    if (filters.lowStock) {
      skus = skus.filter(sku => sku.currentStock <= sku.reorderLevel);
    }
    
    if (filters.vendorId) {
      skus = skus.filter(sku => 
        sku.linkedVendors.some(vendor => vendor.vendorId === filters.vendorId)
      );
    }
    
    // Apply sorting
    skus = applySorting(skus, filters.sortBy, filters.sortOrder);
    
    // Apply pagination
    const { items, total } = applyPagination(skus, filters.page, filters.limit);
    
    return {
      data: items,
      success: true,
      total,
      page: filters.page || 1,
      limit: filters.limit || 10
    };
  }

  static async getSKUById(id: number): Promise<ApiResponse<SKU | null>> {
    await simulateDelay();
    
    const skus = StorageManager.load<SKU[]>('SKUS', []) || [];
    const sku = skus.find(s => s.id === id);
    
    return {
      data: sku || null,
      success: !!sku,
      message: sku ? 'SKU found' : 'SKU not found'
    };
  }

  static async createSKU(skuData: Omit<SKU, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<SKU>> {
    await simulateDelay(600);
    
    const skus = StorageManager.load<SKU[]>('SKUS', []) || [];
    const newId = Math.max(...skus.map(s => s.id), 0) + 1;
    
    const newSKU: SKU = {
      ...skuData,
      id: newId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updatedSKUs = [...skus, newSKU];
    StorageManager.save('SKUS', updatedSKUs);
    
    return {
      data: newSKU,
      success: true,
      message: 'SKU created successfully'
    };
  }

  static async updateSKU(id: number, skuData: Partial<SKU>): Promise<ApiResponse<SKU | null>> {
    await simulateDelay(500);
    
    const skus = StorageManager.load<SKU[]>('SKUS', []) || [];
    const skuIndex = skus.findIndex(s => s.id === id);
    
    if (skuIndex === -1) {
      return {
        data: null,
        success: false,
        message: 'SKU not found'
      };
    }
    
    const updatedSKU = {
      ...skus[skuIndex],
      ...skuData,
      id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString()
    };
    
    skus[skuIndex] = updatedSKU;
    StorageManager.save('SKUS', skus);
    
    return {
      data: updatedSKU,
      success: true,
      message: 'SKU updated successfully'
    };
  }

  static async deleteSKU(id: number): Promise<ApiResponse<boolean>> {
    await simulateDelay(400);
    
    const skus = StorageManager.load<SKU[]>('SKUS', []) || [];
    const filteredSKUs = skus.filter(s => s.id !== id);
    
    if (filteredSKUs.length === skus.length) {
      return {
        data: false,
        success: false,
        message: 'SKU not found'
      };
    }
    
    StorageManager.save('SKUS', filteredSKUs);
    
    return {
      data: true,
      success: true,
      message: 'SKU deleted successfully'
    };
  }

  // Vendor Management
  static async getVendors(filters: VendorFilters = {}): Promise<ApiResponse<Vendor[]>> {
    await simulateDelay();
    
    let vendors = StorageManager.load<Vendor[]>('VENDORS', []) || [];
    
    // Only initialize with mock data if localStorage is completely empty
    if (vendors.length === 0) {
      vendors = [...mockVendors];
      StorageManager.save('VENDORS', vendors);
    }
    
    // Apply filters
    if (filters.search) {
      const search = filters.search.toLowerCase();
      vendors = vendors.filter(vendor => 
        vendor.name.toLowerCase().includes(search) ||
        vendor.vendorCode.toLowerCase().includes(search) ||
        vendor.contactDetails.email.toLowerCase().includes(search)
      );
    }
    
    if (filters.status && filters.status !== 'all') {
      vendors = vendors.filter(vendor => 
        filters.status === 'active' ? vendor.isActive : !vendor.isActive
      );
    }
    
    if (filters.performanceRating) {
      vendors = vendors.filter(vendor => vendor.performanceRating >= filters.performanceRating!);
    }
    
    // Apply sorting
    vendors = applySorting(vendors, filters.sortBy, filters.sortOrder);
    
    // Apply pagination
    const { items, total } = applyPagination(vendors, filters.page, filters.limit);
    
    return {
      data: items,
      success: true,
      total,
      page: filters.page || 1,
      limit: filters.limit || 10
    };
  }

  static async getVendorById(id: number): Promise<ApiResponse<Vendor | null>> {
    await simulateDelay();
    
    const vendors = StorageManager.load<Vendor[]>('VENDORS', []) || [];
    const vendor = vendors.find(v => v.id === id);
    
    return {
      data: vendor || null,
      success: !!vendor,
      message: vendor ? 'Vendor found' : 'Vendor not found'
    };
  }

  static async createVendor(vendorData: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Vendor>> {
    await simulateDelay(700);
    
    const vendors = StorageManager.load<Vendor[]>('VENDORS', []) || [];
    const newId = Math.max(...vendors.map(v => v.id), 0) + 1;
    
    const newVendor: Vendor = {
      ...vendorData,
      id: newId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updatedVendors = [...vendors, newVendor];
    StorageManager.save('VENDORS', updatedVendors);
    
    return {
      data: newVendor,
      success: true,
      message: 'Vendor created successfully'
    };
  }

  static async updateVendor(id: number, vendorData: Partial<Vendor>): Promise<ApiResponse<Vendor | null>> {
    await simulateDelay(500);
    
    const vendors = StorageManager.load<Vendor[]>('VENDORS', []) || [];
    const vendorIndex = vendors.findIndex(v => v.id === id);
    
    if (vendorIndex === -1) {
      return {
        data: null,
        success: false,
        message: 'Vendor not found'
      };
    }
    
    const updatedVendor = {
      ...vendors[vendorIndex],
      ...vendorData,
      id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString()
    };
    
    vendors[vendorIndex] = updatedVendor;
    StorageManager.save('VENDORS', vendors);
    
    return {
      data: updatedVendor,
      success: true,
      message: 'Vendor updated successfully'
    };
  }

  // Purchase Order Management
  static async getPurchaseOrders(filters: POFilters = {}): Promise<ApiResponse<PurchaseOrder[]>> {
    await simulateDelay();
    
    // Load from StorageManager to stay consistent with app state keys
    let pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []) || [];
    
    // Apply filters
    if (filters.status) {
      pos = pos.filter(po => po.status === filters.status);
    }
    
    if (filters.vendorId) {
      pos = pos.filter(po => po.vendorId === filters.vendorId);
    }
    
    if (filters.priority) {
      pos = pos.filter(po => po.priority === filters.priority);
    }
    
    if (filters.createdBy) {
      pos = pos.filter(po => po.createdBy === filters.createdBy);
    }
    
    if (filters.dateFrom) {
      pos = pos.filter(po => new Date(po.createdAt) >= new Date(filters.dateFrom!));
    }
    
    if (filters.dateTo) {
      pos = pos.filter(po => new Date(po.createdAt) <= new Date(filters.dateTo!));
    }
    
    // Apply sorting
    pos = applySorting(pos, filters.sortBy, filters.sortOrder);

    // Only paginate if caller explicitly provides page or limit.
    // Otherwise, return full list to avoid truncating saved data to 10 items.
    const shouldPaginate = typeof filters.page !== 'undefined' || typeof filters.limit !== 'undefined';
    const itemsResult = shouldPaginate ? applyPagination(pos, filters.page, filters.limit) : { items: pos, total: pos.length };

    return {
      data: itemsResult.items,
      success: true,
      total: itemsResult.total,
      page: filters.page || 1,
      limit: filters.limit || pos.length
    };
  }

  static async createPurchaseOrder(poData: Omit<PurchaseOrder, 'id' | 'poNumber' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<PurchaseOrder>> {
    await simulateDelay(800);
    
    // Load from StorageManager to stay consistent with app state keys
    const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []) || [];
    
    // Generate unique ID and PO number
    const existingIds = pos.map(po => po.id).sort((a, b) => a - b);
    const existingPONumbers = pos.map(po => po.poNumber);
    const year = new Date().getFullYear();
    
    let newId = 1;
    let poNumber = '';
    
    // Start from a higher base to avoid conflicts with mock data (mock data has IDs 1-9)
    const mockDataMaxId = 9;
    newId = Math.max(mockDataMaxId + 1, newId);
    
    // Find the next available ID starting from our base
    for (const id of existingIds) {
      if (id === newId) {
        newId++;
      } else if (id > newId) {
        // Found a gap, use the current newId
        break;
      }
    }
    
    // If no gaps found, use the next sequential ID after the highest existing ID
    if (existingIds.length > 0 && newId <= Math.max(...existingIds)) {
      newId = Math.max(...existingIds) + 1;
    }
    
    // Generate PO number and ensure it's unique
    let poNumberSuffix = String(newId).padStart(3, '0');
    poNumber = `PO-${year}-${poNumberSuffix}`;
    
    // Check if PO number already exists, if so, find next available
    let counter = newId;
    while (existingPONumbers.includes(poNumber)) {
      counter++;
      poNumberSuffix = String(counter).padStart(3, '0');
      poNumber = `PO-${year}-${poNumberSuffix}`;
    }
    
    // Update newId to match the PO number if we had to increment
    if (counter > newId) {
      newId = counter;
    }
    
    console.log('Creating new PO with ID:', newId, 'PO Number:', poNumber);
    console.log('Existing PO IDs:', existingIds);
    console.log('Existing PO Numbers:', existingPONumbers);
    
    // Calculate totalAmount from items to ensure accuracy
    const calculatedTotalAmount = poData.items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
    
    const newPO: PurchaseOrder = {
      ...poData,
      id: newId,
      poNumber,
      totalAmount: calculatedTotalAmount, // Use calculated amount
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Persist via StorageManager to same key used across the app
    const updatedPOs = [...pos, newPO];
    StorageManager.save('PURCHASE_ORDERS', updatedPOs);
    
    return {
      data: newPO,
      success: true,
      message: 'Purchase order created successfully'
    };
  }

  static async updatePurchaseOrder(id: number, poData: Partial<PurchaseOrder>): Promise<ApiResponse<PurchaseOrder | null>> {
    await simulateDelay(500);
    
    const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []) || [];
    const poIndex = pos.findIndex(po => po.id === id);
    
    if (poIndex === -1) {
      return {
        data: null,
        success: false,
        message: 'Purchase order not found'
      };
    }
    
    const updatedPO = {
      ...pos[poIndex],
      ...poData,
      id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString()
    };
    
    pos[poIndex] = updatedPO;
    StorageManager.save('PURCHASE_ORDERS', pos);
    
    return {
      data: updatedPO,
      success: true,
      message: 'Purchase order updated successfully'
    };
  }

  static async deletePurchaseOrder(id: number): Promise<ApiResponse<boolean>> {
    await simulateDelay(500);
    
    const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []) || [];
    const poIndex = pos.findIndex(po => po.id === id);
    
    if (poIndex === -1) {
      return {
        data: false,
        success: false,
        message: 'Purchase order not found'
      };
    }
    
    pos.splice(poIndex, 1);
    StorageManager.save('PURCHASE_ORDERS', pos);
    
    return {
      data: true,
      success: true,
      message: 'Purchase order deleted successfully'
    };
  }

  // Quality Inspections
  static async getQualityInspections(filters: { poId?: number } = {}): Promise<ApiResponse<QualityInspection[]>> {
    await simulateDelay();
    
    let inspections = StorageManager.load<QualityInspection[]>('QUALITY_INSPECTIONS', []) || [];
    
    // Only initialize with mock data if localStorage is completely empty
    if (inspections.length === 0) {
      inspections = [...mockQualityInspections];
      StorageManager.save('QUALITY_INSPECTIONS', inspections);
    }
    
    if (filters.poId) {
      inspections = inspections.filter(inspection => inspection.poId === filters.poId);
    }
    
    return {
      data: inspections,
      success: true,
      total: inspections.length
    };
  }

  static async createQualityInspection(inspectionData: Omit<QualityInspection, 'id'>): Promise<ApiResponse<QualityInspection>> {
    await simulateDelay(600);
    
    const inspections = StorageManager.load<QualityInspection[]>('QUALITY_INSPECTIONS', []) || [];
    const newId = Math.max(...inspections.map(i => i.id), 0) + 1;
    
    const newInspection: QualityInspection = {
      ...inspectionData,
      id: newId
    };
    
    const updatedInspections = [...inspections, newInspection];
    StorageManager.save('QUALITY_INSPECTIONS', updatedInspections);
    
    return {
      data: newInspection,
      success: true,
      message: 'Quality inspection created successfully'
    };
  }

  // Invoices
  static async getInvoices(filters: { poId?: number; vendorId?: number; status?: string } = {}): Promise<ApiResponse<Invoice[]>> {
    await simulateDelay();
    
    // Use MockAPI for persistent data
    let invoices = MockAPI.getInvoices();
    
    if (filters.poId) {
      invoices = invoices.filter(invoice => invoice.poId === filters.poId);
    }
    
    if (filters.vendorId) {
      invoices = invoices.filter(invoice => invoice.vendorId === filters.vendorId);
    }
    
    if (filters.status) {
      invoices = invoices.filter(invoice => invoice.status === filters.status);
    }
    
    return {
      data: invoices,
      success: true,
      total: invoices.length
    };
  }

  // Users
  static async getUsers(): Promise<ApiResponse<User[]>> {
    await simulateDelay();
    
    let users = StorageManager.load<User[]>('USERS', []) || [];
    
    // Only initialize with mock data if localStorage is completely empty
    if (users.length === 0) {
      users = [...mockUsers];
      StorageManager.save('USERS', users);
    }
    
    return {
      data: users,
      success: true,
      total: users.length
    };
  }

  static async createInvoice(invoiceData: Omit<Invoice, 'id' | 'createdAt'>): Promise<ApiResponse<Invoice>> {
    await simulateDelay(500);
    
    // Use MockAPI for persistent data
    const invoices = MockAPI.getInvoices();
    // Start from higher base to avoid conflicts with mock data (mock invoices have IDs 1-5)
    const mockDataMaxId = 5;
    const existingIds = invoices.map(i => i.id).sort((a, b) => a - b);
    let newId = Math.max(mockDataMaxId + 1, 1);
    
    for (const id of existingIds) {
      if (id === newId) {
        newId++;
      } else if (id > newId) {
        break;
      }
    }
    
    // If no gaps found, use the next sequential ID after the highest existing ID
    if (existingIds.length > 0 && newId <= Math.max(...existingIds)) {
      newId = Math.max(...existingIds) + 1;
    }
    
    const newInvoice: Invoice = {
      ...invoiceData,
      id: newId,
      status: invoiceData.status || 'Created', // Set default status to 'Created'
      createdAt: new Date().toISOString()
    };
    
    // Use MockAPI to persist the new invoice
    MockAPI.createInvoice(newInvoice);
    
    return {
      data: newInvoice,
      success: true,
      message: 'Invoice created successfully'
    };
  }

  static async updateInvoice(id: number, invoiceData: Partial<Invoice>): Promise<ApiResponse<Invoice>> {
    await simulateDelay(500);
    
    const invoices = MockAPI.getInvoices();
    const invoiceIndex = invoices.findIndex(invoice => invoice.id === id);
    
    console.log('MockAPIService - updateInvoice called for ID:', id, 'with data:', invoiceData);
    console.log('MockAPIService - Found invoice at index:', invoiceIndex, 'Current status:', invoices[invoiceIndex]?.status);
    
    if (invoiceIndex === -1) {
      console.log('MockAPIService - Invoice not found');
      return {
        data: null,
        success: false,
        message: 'Invoice not found'
      };
    }
    
    const updatedInvoice = {
      ...invoices[invoiceIndex],
      ...invoiceData,
      id,
      updatedAt: new Date().toISOString()
    };
    
    console.log('MockAPIService - Updated invoice:', updatedInvoice);
    console.log('MockAPIService - New status:', updatedInvoice.status);
    
    // Use MockAPI to persist the update
    MockAPI.updateInvoice(updatedInvoice);
    
    console.log('MockAPIService - Invoice saved to localStorage');
    
    return {
      data: updatedInvoice,
      success: true,
      message: 'Invoice updated successfully'
    };
  }

  static async deleteInvoice(id: number): Promise<ApiResponse<boolean>> {
    await simulateDelay(500);
    
    const invoices = StorageManager.load<Invoice[]>('INVOICES', []) || [];
    const updatedInvoices = invoices.filter(invoice => invoice.id !== id);
    
    if (updatedInvoices.length === invoices.length) {
      return {
        data: false,
        success: false,
        message: 'Invoice not found'
      };
    }
    
    StorageManager.save('INVOICES', updatedInvoices);
    
    return {
      data: true,
      success: true,
      message: 'Invoice deleted successfully'
    };
  }

  // Dashboard Statistics
  static async getDashboardStats(): Promise<ApiResponse<any>> {
    await simulateDelay();
    
    const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []) || [];
    const skus = StorageManager.load<SKU[]>('SKUS', []) || [];
    const vendors = StorageManager.load<Vendor[]>('VENDORS', []) || [];
    const inspections = StorageManager.load<QualityInspection[]>('QUALITY_INSPECTIONS', []) || [];
    const invoices = StorageManager.load<Invoice[]>('INVOICES', []) || [];
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // Calculate statistics
    const thisMonthPOs = pos.filter(po => {
      const poDate = new Date(po.createdAt);
      return poDate.getMonth() === currentMonth && poDate.getFullYear() === currentYear;
    });
    
    const pendingApprovals = pos.filter(po => po.status === 'CREATED');
    const totalSpend = thisMonthPOs.reduce((sum, po) => sum + po.totalAmount, 0);
    const qualityIssues = inspections.filter(i => i.overallStatus === 'Failed').length;
    const onTimeDeliveries = pos.filter(po => po.deliveryDetails.actualDate && po.status === 'FULLY_RECEIVED').length;
    const totalCompletedPOs = pos.filter(po => po.status === 'FULLY_RECEIVED').length;
    const activeSKUs = skus.filter(sku => sku.isActive).length;
    const averageVendorRating = vendors.reduce((sum, v) => sum + v.performanceRating, 0) / vendors.length;
    
    const stats = {
      totalPOsThisMonth: thisMonthPOs.length,
      pendingApprovals: pendingApprovals.length,
      totalSpend,
      qualityIssues,
      onTimeDeliveryRate: totalCompletedPOs > 0 ? (onTimeDeliveries / totalCompletedPOs) * 100 : 0,
      activeSKUs,
      averageVendorRating: Math.round(averageVendorRating * 10) / 10,
      costSavings: Math.floor(Math.random() * 50000) + 10000 // Mock calculation
    };
    
    return {
      data: stats,
      success: true
    };
  }

  // Utility methods
  static async resetAllData(): Promise<ApiResponse<boolean>> {
    await simulateDelay(1000);
    
    StorageManager.clearAll();
    initializeData();
    
    return {
      data: true,
      success: true,
      message: 'All data reset to defaults'
    };
  }

  static async exportData(): Promise<ApiResponse<string>> {
    await simulateDelay(500);
    
    const exportData = StorageManager.exportData();
    
    return {
      data: exportData,
      success: true,
      message: 'Data exported successfully'
    };
  }

  static async importData(jsonData: string): Promise<ApiResponse<boolean>> {
    await simulateDelay(800);
    
    const success = StorageManager.importData(jsonData);
    
    return {
      data: success,
      success,
      message: success ? 'Data imported successfully' : 'Failed to import data'
    };
  }

  // Reset only invoices to mock data
  static async resetInvoicesToMock(): Promise<ApiResponse<boolean>> {
    await simulateDelay(300);
    try {
      MockAPI.clearDataFromLocal('invoices');
      MockAPI.getInvoices(); // This will reinitialize with mock data
      return { data: true, success: true, message: 'Invoices reset to mock data' };
    } catch (error) {
      console.error('Failed to reset invoices to mock data', error);
      return { data: false, success: false, message: 'Failed to reset invoices' };
    }
  }

  // Reset only purchase orders to mock data
  static async resetPurchaseOrdersToMock(): Promise<ApiResponse<boolean>> {
    await simulateDelay(300);
    try {
      MockAPI.clearDataFromLocal('purchaseOrders');
      MockAPI.getPOs(); // This will reinitialize with mock data
      return { data: true, success: true, message: 'Purchase Orders reset to mock data' };
    } catch (error) {
      console.error('Failed to reset POs to mock data', error);
      return { data: false, success: false, message: 'Failed to reset purchase orders' };
    }
  }

  // Force refresh all mock data (useful for development)
  static async refreshMockData(): Promise<ApiResponse<boolean>> {
    try {
      console.log('Starting mock data refresh...');
      console.log('Mock SKUs count:', mockSKUs.length);
      
      // Clear existing data first
      StorageManager.remove('SKUS');
      StorageManager.remove('VENDORS');
      StorageManager.remove('PURCHASE_ORDERS');
      StorageManager.remove('QUALITY_INSPECTIONS');
      StorageManager.remove('INVOICES');
      StorageManager.remove('GATE_ENTRIES');
      StorageManager.remove('USERS');
      
      // Save fresh mock data with updated status values
      StorageManager.save('SKUS', [...mockSKUs]);
      StorageManager.save('VENDORS', [...mockVendors]);
      StorageManager.save('PURCHASE_ORDERS', [...mockPurchaseOrders]);
      StorageManager.save('QUALITY_INSPECTIONS', [...mockQualityInspections]);
      StorageManager.save('INVOICES', [...mockInvoices]);
      StorageManager.save('GATE_ENTRIES', [...mockGateEntries]);
      StorageManager.save('USERS', [...mockUsers]);
      
      // Verify the data was saved
      const savedSKUs = StorageManager.load('SKUS', []);
      const savedPOs = StorageManager.load('PURCHASE_ORDERS', []);
      console.log('Saved SKUs count:', savedSKUs?.length || 0);
      console.log('Saved POs count:', savedPOs?.length || 0);
      
      return {
        data: true,
        success: true,
        message: 'Mock data refreshed successfully'
      };
    } catch (error) {
      console.error('Error refreshing mock data:', error);
      return {
        data: false,
        success: false,
        message: 'Failed to refresh mock data'
      };
    }
  }

  // Check if data exists in localStorage
  static hasData(): boolean {
    return StorageManager.exists('SKUS') || 
           StorageManager.exists('VENDORS') || 
           StorageManager.exists('PURCHASE_ORDERS') ||
           StorageManager.exists('USERS');
  }

  // PO Approval Management
  static async approvePO(poId: number, action: string, notes?: string, userId?: number): Promise<ApiResponse<boolean>> {
    await simulateDelay();
    
    try {
      const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []);
      const poIndex = pos.findIndex(po => po.id === poId);
      
      if (poIndex === -1) {
        return {
          data: false,
          success: false,
          message: 'Purchase Order not found'
        };
      }
      
      let newStatus = '';
      switch (action) {
        case 'approve':
          newStatus = 'APPROVED';
          break;
        case 'reject':
          newStatus = 'REJECTED';
          break;
        case 'request_changes':
          newStatus = 'NEEDS_REVISION';
          break;
        default:
          return {
            data: false,
            success: false,
            message: 'Invalid action'
          };
      }
      
      // Update PO status
      pos[poIndex] = {
        ...pos[poIndex],
        status: newStatus as any,
        updatedAt: new Date().toISOString(),
        notes: notes ? `${pos[poIndex].notes || ''}\nApproval Notes: ${notes}`.trim() : pos[poIndex].notes
      };
      
      StorageManager.save('PURCHASE_ORDERS', pos);
      
      return {
        data: true,
        success: true,
        message: `Purchase Order ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'returned for changes'} successfully`
      };
    } catch (error) {
      console.error('Error approving PO:', error);
      return {
        data: false,
        success: false,
        message: 'Failed to approve Purchase Order'
      };
    }
  }

  // Invoice Verification Management
  static async verifyInvoice(invoiceId: number, verified: boolean, notes?: string, userId?: number): Promise<ApiResponse<boolean>> {
    await simulateDelay();
    
    try {
      const invoices = StorageManager.load<Invoice[]>('INVOICES', []);
      const invoiceIndex = invoices.findIndex(inv => inv.id === invoiceId);
      
      if (invoiceIndex === -1) {
        return {
          data: false,
          success: false,
          message: 'Invoice not found'
        };
      }
      
      // Update invoice status
      invoices[invoiceIndex] = {
        ...invoices[invoiceIndex],
        status: verified ? 'Approved' : 'Rejected',
        notes: notes,
        updatedAt: new Date().toISOString()
      };
      
      StorageManager.save('INVOICES', invoices);
      
      // If verified, update related PO to APPROVED
      if (verified) {
        const invoice = invoices[invoiceIndex];
        const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []);
        const poIndex = pos.findIndex(po => po.id === invoice.poId);
        
        if (poIndex !== -1) {
          pos[poIndex] = {
            ...pos[poIndex],
            status: 'APPROVED',
            securityVerified: true,
            securityVerifiedBy: userId || 1,
            securityVerifiedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          StorageManager.save('PURCHASE_ORDERS', pos);
        }
      }
      
      return {
        data: true,
        success: true,
        message: verified ? 'Invoice verified successfully' : 'Invoice rejected'
      };
    } catch (error) {
      console.error('Error verifying invoice:', error);
      return {
        data: false,
        success: false,
        message: 'Failed to verify invoice'
      };
    }
  }

  // IQC Testing Management
  static async completeIQCTesting(poId: number, testResults: any, userId?: number): Promise<ApiResponse<boolean>> {
    await simulateDelay();
    
    try {
      const pos = StorageManager.load<PurchaseOrder[]>('PURCHASE_ORDERS', []);
      const poIndex = pos.findIndex(po => po.id === poId);
      
      if (poIndex === -1) {
        return {
          data: false,
          success: false,
          message: 'Purchase Order not found'
        };
      }
      
      // Calculate overall status based on test results
      let overallStatus = '';
      const allItems = testResults.items;
      const fullyAccepted = allItems.every((item: any) => item.rejectedQty === 0);
      const fullyRejected = allItems.every((item: any) => item.acceptedQty === 0);
      
      if (fullyAccepted) {
        overallStatus = 'Fully Accepted';
      } else if (fullyRejected) {
        overallStatus = 'Fully Rejected';
      } else {
        overallStatus = 'Partly Accepted';
      }
      
      // Update PO with IQC results
      const updatedItems = testResults.items.map((item: any) => ({
        ...item,
        iqcTested: true,
        iqcTestedBy: userId || 1,
        iqcTestedAt: new Date().toISOString(),
        iqcNotes: item.notes || ''
      }));
      
      pos[poIndex] = {
        ...pos[poIndex],
        items: updatedItems,
        status: overallStatus as any,
        iqcCompleted: true,
        iqcCompletedBy: userId || 1,
        iqcCompletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      StorageManager.save('PURCHASE_ORDERS', pos);
      
      return {
        data: true,
        success: true,
        message: `IQC testing completed. Status: ${overallStatus}`
      };
    } catch (error) {
      console.error('Error completing IQC testing:', error);
      return {
        data: false,
        success: false,
        message: 'Failed to complete IQC testing'
      };
    }
  }

  // Vendor Invoice Management
  static async createVendorInvoice(invoiceData: any): Promise<ApiResponse<any>> {
    await simulateDelay(500);
    
    try {
      const invoices = StorageManager.load<any[]>('INVOICES', []) || [];
      
      // Generate unique ID starting from higher base to avoid conflicts with mock data
      const existingIds = invoices.map(inv => inv.id).sort((a, b) => a - b);
      const mockDataMaxId = 5; // Mock invoices have IDs 1-5
      let newId = Math.max(mockDataMaxId + 1, 1);
      
      for (const id of existingIds) {
        if (id === newId) {
          newId++;
        } else if (id > newId) {
          break;
        }
      }
      
      // If no gaps found, use the next sequential ID after the highest existing ID
      if (existingIds.length > 0 && newId <= Math.max(...existingIds)) {
        newId = Math.max(...existingIds) + 1;
      }
      
      const newInvoice = {
        ...invoiceData,
        id: newId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      invoices.push(newInvoice);
      StorageManager.save('INVOICES', invoices);
      
      console.log('MockAPIService - Created vendor invoice:', newInvoice);
      
      return {
        data: newInvoice,
        success: true,
        message: 'Vendor invoice created successfully'
      };
    } catch (error) {
      console.error('Error creating vendor invoice:', error);
      return {
        data: null,
        success: false,
        message: 'Failed to create vendor invoice'
      };
    }
  }


  static async updateQualityInspection(id: number, inspectionData: any): Promise<ApiResponse<any>> {
    await simulateDelay(500);
    try {
      const inspections = StorageManager.load<any[]>('QUALITY_INSPECTIONS', []) || [];
      const inspectionIndex = inspections.findIndex(ins => ins.id === id);
      
      if (inspectionIndex === -1) {
        return {
          data: null,
          success: false,
          message: 'Quality inspection not found'
        };
      }

      const updatedInspection = {
        ...inspections[inspectionIndex],
        ...inspectionData,
        updatedAt: new Date().toISOString()
      };
      
      inspections[inspectionIndex] = updatedInspection;
      StorageManager.save('QUALITY_INSPECTIONS', inspections);
      
      return {
        data: updatedInspection,
        success: true,
        message: 'Quality inspection updated successfully'
      };
    } catch (error) {
      console.error('Error updating quality inspection:', error);
      return {
        data: null,
        success: false,
        message: 'Failed to update quality inspection'
      };
    }
  }

  static async deleteQualityInspection(id: number): Promise<ApiResponse<boolean>> {
    await simulateDelay(500);
    try {
      const inspections = StorageManager.load<any[]>('QUALITY_INSPECTIONS', []) || [];
      const filteredInspections = inspections.filter(ins => ins.id !== id);
      
      if (filteredInspections.length === inspections.length) {
        return {
          data: false,
          success: false,
          message: 'Quality inspection not found'
        };
      }
      
      StorageManager.save('QUALITY_INSPECTIONS', filteredInspections);
      
      return {
        data: true,
        success: true,
        message: 'Quality inspection deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting quality inspection:', error);
      return {
        data: false,
        success: false,
        message: 'Failed to delete quality inspection'
      };
    }
  }

  // Handover Logs
  static async getHandoverLogs(): Promise<ApiResponse<HandoverLog[]>> {
    await simulateDelay();
    
    const handoverLogs = StorageManager.load<HandoverLog[]>('HANDOVER_LOGS', []) || [];
    
    return {
      data: handoverLogs,
      success: true,
      message: 'Handover logs fetched successfully'
    };
  }

  static async createHandoverLog(handoverData: Omit<HandoverLog, 'id' | 'handoverId' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<HandoverLog>> {
    await simulateDelay(500);
    
    const handoverLogs = StorageManager.load<HandoverLog[]>('HANDOVER_LOGS', []) || [];
    const newId = Math.max(...handoverLogs.map(h => h.id), 0) + 1;
    const handoverId = `HOV-${new Date().getFullYear()}-${String(newId).padStart(3, '0')}`;
    
    const newHandoverLog: HandoverLog = {
      ...handoverData,
      id: newId,
      handoverId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updatedHandoverLogs = [...handoverLogs, newHandoverLog];
    StorageManager.save('HANDOVER_LOGS', updatedHandoverLogs);
    
    return {
      data: newHandoverLog,
      success: true,
      message: 'Handover log created successfully'
    };
  }

  static async updateHandoverLog(id: number, handoverData: Partial<HandoverLog>): Promise<ApiResponse<HandoverLog>> {
    await simulateDelay(500);
    
    const handoverLogs = StorageManager.load<HandoverLog[]>('HANDOVER_LOGS', []) || [];
    const handoverLogIndex = handoverLogs.findIndex(h => h.id === id);
    
    if (handoverLogIndex === -1) {
      return {
        data: null as any,
        success: false,
        message: 'Handover log not found'
      };
    }
    
    const updatedHandoverLog = {
      ...handoverLogs[handoverLogIndex],
      ...handoverData,
      updatedAt: new Date().toISOString()
    };
    
    handoverLogs[handoverLogIndex] = updatedHandoverLog;
    StorageManager.save('HANDOVER_LOGS', handoverLogs);
    
    return {
      data: updatedHandoverLog,
      success: true,
      message: 'Handover log updated successfully'
    };
  }
}

// Initialize the service when module loads
MockAPIService.initialize();
